<template>
    <div class="tw:space-y-5">
        <div v-if="wizardStore.tw" class="tw:text-xs tw:text-zinc-500">
            {{ $t("Felter markert med stjerne (*) er obligatoriske.") }}
        </div>
        <small v-if="!wizardStore.tw">
            {{ $t("Felter markert med stjerne (*) er obligatoriske.") }}
        </small>

        <!-- Basic fields -->
        <div class="tw:space-y-4">
            <FormTextInput
                :tw="wizardStore.tw"
                :label="wizardStore.getStudentMobileLabel + '*'"
                :name="'mobile'"
                :type="'tel'"
                :placeholder="wizardStore.getStudentMobileLabel"
                :maxlength="10"
                :disabled="wizardStore.isLoadingStudent"
                v-model="wizardStore.student.mobile"
                :error="wizardStore.student.errors.mobile"
            />
            <FormTextInput
                :tw="wizardStore.tw"
                :label="wizardStore.getStudentFnrLabel + '*'"
                :name="'fnr'"
                :type="'tel'"
                :placeholder="wizardStore.nnidPlaceholder"
                :disabled="wizardStore.isLoadingStudent"
                v-model="wizardStore.student.fnr"
                :error="wizardStore.student.errors.fnr"
            />
        </div>

        <!-- Extended fields -->
        <div v-if="wizardStore.showStudentDetailFields" class="tw:space-y-4">

            <FormTextInput
                :tw="wizardStore.tw"
                :label="$t('Fornavn') + '*'"
                :name="'first_name'"
                :placeholder="$t('Fornavn')"
                :disabled="!!(wizardStore.student.lookupResponse && wizardStore.student.lookupResponse.first_name)"
                v-model="wizardStore.student.firstName"
                :required="true"
                :error="wizardStore.student.errors.firstName"
            />

            <FormTextInput
                :tw="wizardStore.tw"
                :label="$t('Etternavn') + '*'"
                :name="'last_name'"
                :placeholder="$t('Etternavn')"
                :disabled="!!(wizardStore.student.lookupResponse && wizardStore.student.lookupResponse.last_name)"
                v-model="wizardStore.student.lastName"
                :required="true"
                :error="wizardStore.student.errors.lastName"
            />

            <FormTextInput
                :tw="wizardStore.tw"
                :label="$t('Adresse') + '*'"
                :name="'address'"
                :placeholder="$t('Adresse')"
                :disabled="!!(wizardStore.student.lookupResponse && wizardStore.student.lookupResponse.address)"
                v-model="wizardStore.student.address"
                :required="true"
                :error="wizardStore.student.errors.address"
            />

            <FormTextInput
                :tw="wizardStore.tw"
                :label="$t('Postnummer') + '*'"
                :name="'postal_code'"
                :placeholder="$t('Postnummer')"
                :disabled="!!(wizardStore.student.lookupResponse && wizardStore.student.lookupResponse.postal_code)"
                v-model="wizardStore.student.postalCode"
                :required="true"
                :error="wizardStore.student.errors.postalCode"
            />

            <FormTextInput
                :tw="wizardStore.tw"
                :label="$t('Sted') + '*'"
                :name="'city'"
                :placeholder="$t('Sted')"
                :disabled="!!(wizardStore.student.lookupResponse && wizardStore.student.lookupResponse.city)"
                v-model="wizardStore.student.city"
                :required="true"
                :error="wizardStore.student.errors.city"
            />

            <FormTextInput
                :tw="wizardStore.tw"
                :label="$t('E-postadresse') + '*'"
                :name="'email'"
                :placeholder="$t('E-postadresse')"
                :disabled="!!(wizardStore.student.lookupResponse && wizardStore.student.lookupResponse.email)"
                v-model="wizardStore.student.email"
                :required="true"
                :error="wizardStore.student.errors.email"
            />

            <FormTextarea
                :tw="wizardStore.tw"
                :label="$t('Kommentar')"
                :name="'comment'"
                :placeholder="$t('Kommentar')"
                v-model="wizardStore.student.comment"
            />

            <div :class="{'form-group': !tw, '': tw}" v-if="wizardStore.deliveryOptions.length">
                <label :class="{'mt-1': !tw, 'tw:block tw:text-sm tw:font-medium tw:text-zinc-800': tw}">{{
                        $t('Valg av levering')
                    }}*</label>
                <div class="form-check" v-for="(option,i) in wizardStore.deliveryOptions">
                    <input
                        :id="'delivery-' + i"
                        :key="'delivery-' + i"
                        class="form-check-input"
                        type="radio"
                        name="deliveryOption"
                        :value="option.value"
                        :disabled="wizardStore.deliveryOptions.length === 1"
                        v-model="wizardStore.selectedDeliveryOption"/>

                    <label class="form-check-label cursor-pointer" :for="'delivery-' + i">
                        {{ option.label }}
                        <small>({{
                                option.shipping_cost ? $t('Frakt') + ': ' + moneyFormat(option.shipping_cost) : $t('Gratis')
                            }})</small>
                    </label>
                </div>
                <div :class="{'form-text text-danger': !tw, 'tw:mt-1 tw:text-sm tw:text-red-600': tw}"
                     v-if="wizardStore.deliveryError">
                    {{ wizardStore.deliveryError }}
                </div>
            </div>

            <div v-if="wizardStore.hasResourcePool">
                <label class="tw:block tw:text-sm tw:font-medium tw:text-zinc-800 tw:mb-1">{{
                        $t('Valg av kjøretøy')
                    }}*</label>
                <p class="tw:mt-0 tw:mb-2 tw:text-xs tw:text-zinc-500">
                    {{ $t("Du må velge hva slags kjøretøy du skal benytte under kurset") }}</p>
                <select name="resource-class-id" v-model="wizardStore.student.resourcePoolClassId"
                        class="tw:block tw:w-full tw:rounded tw:border tw:border-zinc-300 tw:bg-white tw:px-3 tw:py-2 tw:text-base tw:text-zinc-800 focus:tw:outline-none focus:tw:ring-2 focus:tw:ring-(--shop-primary) focus:tw:border-(--shop-primary)">
                    <option value="0" disabled selected>- {{ $t("Velg") }} -</option>
                    <option :value="pool.class_id"
                            :disabled="pool.free_slots <= wizardStore.getResourcePoolClassIdsArray.filter(id => id === pool.class_id).length"
                            v-for="pool in wizardStore.availableResourcePools">
                        {{ pool.classes_meta.resource_pool_class_name_on_web || pool.class_name }}
                        <span
                            v-if="pool.free_slots <= wizardStore.getResourcePoolClassIdsArray.filter(id => id === pool.class_id).length">({{
                                $t("Ingen flere ledige plasser")
                            }})</span>
                    </option>
                </select>
            </div>
            <div v-if="wizardStore.freeInterpreterSlots > 0" class="tw:flex tw:items-center tw:gap-2">
                <input id="with-interpreter"
                       class="tw:h-4 tw:w-4 tw:rounded tw:border-zinc-300 focus:tw:ring-(--shop-primary)"
                       type="checkbox" value="1" name="with-interpreter"
                       :disabled="!wizardStore.hasFreeInterpreterSlots && !wizardStore.student.withInterpreter"
                       v-model="wizardStore.student.withInterpreter"/>
                <label for="with-interpreter" class="tw:text-sm tw:text-zinc-800">{{ $t("Med tolk") }}</label>
            </div>
        </div>

        <div v-if="noStudentLookupResponse"
             :class="{'btn btn-link': !wizardStore.tw, 'tw:text-sm tw:text-(--shop-primary) tw:underline tw:cursor-pointer': wizardStore.tw}"
             @click="wizardStore.toggleManuallyRegisterStudent">{{ wizardStore.toggleManuallyRegisteredStudentLabel }}
        </div>

        <loadingSpinner :isLoading="wizardStore.isLoadingStudent" :tw="tw"/>
        <div v-if="wizardStore.student.errors.lookup"
             class="tw:rounded-md tw:border tw:border-red-200 tw:bg-red-50 tw:p-3 tw:text-sm tw:text-red-700">
            {{ wizardStore.student.errors.lookup }}
        </div>
    </div>
</template>
<script setup>
import {useWizardStore} from './wizardState';
import loadingSpinner from './loadingSpinner.vue';
import {computed, watch} from 'vue';
import FormTextInput from './FormTextInput.vue'
import FormTextarea from './FormTextarea.vue'

const tw = computed(() => wizardStore.tw)

const wizardStore = useWizardStore();

const noStudentLookupResponse = computed(() => {
    return !wizardStore.student.lookupResponse || Object.keys(wizardStore.student.lookupResponse).length === 0;
});

const validateAndLookup = () => {
    if (wizardStore.student.errors && wizardStore.student.errors.lookup) {
        delete wizardStore.student.errors.lookup;
    }
    if (!wizardStore.studentHasValidFnrAndMobile()) {
        wizardStore.disableManuallyRegisterStudent()
        return;
    }
    wizardStore.resetStudentLookupResponse();
    wizardStore.validateAndLookupStudent();
}

const moneyFormat = (amount) => {
    return window.TABS.L10n.money.parse(amount).format();
}

watch(() => wizardStore.student.fnr, () => {
    validateAndLookup()
});

watch(() => wizardStore.student.mobile, () => {
    validateAndLookup()
});
</script>